import { useState } from "react";
import { motion } from "framer-motion";
import { Particles } from "@tsparticles/react";
import { ArrowRightIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { ctaParticlesConfig } from "../config/particles";
import { cn } from "../utils/cn";
import { useSettings } from "../contexts/SettingsContext";
import WaitlistModal from "./WaitlistModal";

export default function CTA() {
  const { settings } = useSettings();
  const [particleKey, setParticleKey] = useState(0);
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);

  const handleCTAClick = () => {
    // Trigger particle burst effect by remounting the component
    setParticleKey((prev) => prev + 1);
    // Open waitlist modal
    setIsWaitlistOpen(true);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  return (
    <section id="cta" className="py-24 relative overflow-hidden">
      {/* Top fade from previous section */}
      <div className="pointer-events-none absolute inset-x-0 -top-16 h-16 bg-gradient-to-b from-transparent to-white/80 dark:to-[#0b0f19]/80 [mask-image:linear-gradient(to_bottom,black,transparent)]" />

      {/* Light mode theme background (kept transparent in dark mode) */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-[#fff7f5] to-white dark:hidden" />
      <div className="absolute inset-0 [background:radial-gradient(60%_40%_at_50%_0%,rgba(233,92,73,0.10),transparent_60%)] dark:hidden" />

      {/* Particle effects container */}
      <div className="absolute inset-0 pointer-events-none">
        <Particles
          key={particleKey}
          id={`cta-particles-${particleKey}`}
          options={ctaParticlesConfig}
        />
      </div>

      {/* Overlay for better text readability */}
      <div className="absolute inset-0 bg-black/10 dark:bg-black/20" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center"
        >
          {/* Icon */}
          <motion.div
            variants={itemVariants}
            className="flex justify-center mb-8"
          >
            <motion.div
              className="w-20 h-20 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center"
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <SparklesIcon className="h-10 w-10 text-black dark:text-white" />
            </motion.div>
          </motion.div>

          {/* Main heading */}
          <motion.h2
            variants={itemVariants}
            className="text-4xl sm:text-5xl lg:text-6xl font-oxanium font-bold mb-6"
            style={{ color: "#e95c49" }}
          >
            {settings.ctaTitle}
          </motion.h2>

          {/* Subheading */}
          <motion.p
            variants={itemVariants}
            className="text-xl sm:text-2xl mb-8 text-black dark:text-white/90 max-w-3xl mx-auto leading-relaxed"
          >
            {settings.ctaSubtitle}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <motion.button
              onClick={handleCTAClick}
              className={cn(
                "group relative px-10 py-5 rounded-xl font-semibold text-lg",
                "text-white",
                "shadow-lg hover:shadow-xl",
                "transition-all duration-300",
                "flex items-center gap-3",
                "overflow-hidden"
              )}
              style={{
                backgroundColor: "#e95c49",
                boxShadow: "0 10px 25px rgba(233, 92, 73, 0.25)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#e95c49";
                e.currentTarget.style.boxShadow =
                  "0 25px 50px rgba(233, 92, 73, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#e95c49";
                e.currentTarget.style.boxShadow =
                  "0 10px 25px rgba(233, 92, 73, 0.25)";
              }}
              whileHover={{
                scale: 1.05,
                boxShadow: "0 25px 50px rgba(233, 92, 73, 0.4)",
              }}
              whileTap={{ scale: 0.98 }}
            >
              {/* Button content */}
              <span className="relative z-10 flex items-center gap-3">
                <span className="text-white">{settings.ctaButtonText}</span>
                <motion.div
                  initial={{ x: 0 }}
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <ArrowRightIcon className="h-5 w-5 text-white" />
                </motion.div>
              </span>

              {/* Shimmer effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                initial={{ x: "-100%" }}
                whileHover={{ x: "100%" }}
                transition={{ duration: 0.6 }}
              />
            </motion.button>

            <motion.a
              href="https://de5.gitbook.io/de5-docs/"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "px-10 py-5 rounded-xl font-semibold text-lg",
                "text-black",
                "backdrop-blur-sm transition-all duration-300",
                "inline-block"
              )}
              style={{
                backgroundColor: "#ffffff",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f5f5f5";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#ffffff";
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              View Documentation
            </motion.a>
          </motion.div>

          {/* Trust indicators */}
          <motion.div variants={itemVariants} className="mt-12 text-center">
            <p className="text-black/70 dark:text-white/70 text-sm mb-4">
              Build on the best DeFi protocols worldwide
            </p>

            {/* Protocol logos */}
            <div className="flex justify-center items-center gap-8 opacity-60">
              {[
                { name: "Ethereum", logo: "⟠" },
                { name: "Uniswap", logo: "🦄" },
                { name: "Arbitrum", logo: "🔺" },
                { name: "0x", logo: "0x" },
              ].map((protocol, index) => (
                <motion.div
                  key={protocol.name}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 backdrop-blur-sm text-sm font-medium text-black dark:text-white"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 0.6, y: 0 }}
                  transition={{ duration: 0.5, delay: 1 + index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ opacity: 1, scale: 1.05 }}
                >
                  <span className="text-lg">{protocol.logo}</span>
                  <span>{protocol.name}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom fade to next section */}
      <div className="pointer-events-none absolute inset-x-0 -bottom-16 h-16 bg-gradient-to-t from-transparent to-white/80 dark:to-[#0b0f19]/80 [mask-image:linear-gradient(to_top,black,transparent)]" />

      {/* Floating elements */}
      <motion.div
        className="absolute top-20 left-20 w-32 h-32 rounded-full bg-white/10 blur-2xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.1, 0.3, 0.1],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <motion.div
        className="absolute bottom-20 right-20 w-24 h-24 rounded-full bg-white/10 blur-2xl"
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Geometric decorations */}
      <div className="absolute top-1/4 right-1/4 w-4 h-4 border border-white/20 rotate-45 animate-spin-slow" />
      <div className="absolute bottom-1/3 left-1/3 w-6 h-6 border border-white/20 rotate-45 animate-pulse" />

      {/* Waitlist Modal */}
      <WaitlistModal
        isOpen={isWaitlistOpen}
        onClose={() => setIsWaitlistOpen(false)}
      />
    </section>
  );
}
